/**
 * @file       Date range component
 * @version    2.0.0
 * @author     Boris Veits
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or 
 *             altered in anyway without expressed written permission from 
 *             MindShare HDV LLC.
 */

import { Component, EventEmitter, Input, OnInit, OnChanges, 
    OnDestroy, SimpleChanges, Output, ViewChild } 
    from '@angular/core';
import { Subscription } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS,
    MAT_NATIVE_DATE_FORMATS } from '@angular/material/core';
import { CustomDateAdapter } from './customDateAdapter';

@Component
({
    selector: 'date-range-picker',
    templateUrl: './dateRange.component.html',
    standalone: false,
    providers:
    [
        { provide: DateAdapter, useClass: CustomDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS }
    ]
})

/**
 * @desc   - Component for date range picker
 */
class DateRangePickerComponent implements OnInit, OnChanges, OnDestroy
{
    @Input() disabled = false;
    @Input() initialValue: { start: Date | null; end: Date | null } | null = 
        null;
    @Input() hideModeSelection = false;

    @Output() dateRangeChange = 
        new EventEmitter<{ start: Date | null; end: Date | null }>();
    @Output() clearPressed = new EventEmitter<void>();

    rangeGroup = new FormGroup(
    {
        start: new FormControl<Date | null>(null),
        end: new FormControl<Date | null>(null)
    });

    subscriptions: Subscription[] = [];

    @ViewChild('picker') picker!: MatDateRangePicker<Date>;

    //______________________ S T A T I C  M E T H O D S _______________________

    //__________________ P R I V A T E  M E T H O D S __________________________

    /**
     * @desc - emit date range change event
     * @access - private
     * @return -
     */
    private emitDateRangeChange(): void
    {
        this.dateRangeChange.emit(
        {
            start: this.startControl.value,
            end: this.endControl.value
        });
    }
    //___________________ P U B L I C  M E T H O D S ___________________________

    get startControl(): FormControl<Date | null>
    {
        return this.rangeGroup.controls['start'] as FormControl<Date | null>;
    }

    get endControl(): FormControl<Date | null>
    {
        return this.rangeGroup.controls['end'] as FormControl<Date | null>;
    }

    get startPicker(): MatDateRangePicker<Date>
    {
        return this.picker;
    }

    get endPicker(): MatDateRangePicker<Date>
    {
        return this.picker;
    }

    /**
     * @desc   - component initialization
     * @access - public
     * @param  -
     * @return -
     */
    ngOnInit() 
    {
        let subscription: Subscription;

        if (this.initialValue)
        {
            this.rangeGroup.patchValue(
            {
                start: this.initialValue.start,
                end: this.initialValue.end
            });
        }

        if (this.disabled)
        {
            this.rangeGroup.disable();
        }

        subscription = this.rangeGroup.valueChanges.subscribe(() => 
        {
            this.emitDateRangeChange();
        });
        this.subscriptions.push(subscription);
    }

    /**
     * @desc   - handle changes to input properties
     * @access - public
     * @param  - changes: SimpleChanges
     * @return -
     */
    ngOnChanges
    (
        changes: SimpleChanges
    ): void
    {
        let initialValueChange: any;
        let cur: { start: Date | null; end: Date | null } | null;

        initialValueChange = changes['initialValue'];
        if (!initialValueChange)
        {
            return;
        }

        cur = initialValueChange.currentValue;

        if (cur && cur.start && cur.end)
        {
            this.rangeGroup.patchValue(
            {
                start: cur.start,
                end: cur.end
            });
        }
    }

    /**
     * @desc   - component destruction
     * @access - public
     * @param  -
     * @return -
     */
    ngOnDestroy(): void
    {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    /**
     * @desc - disable the date range
     * @access - public
     * @param -
     * @return -
     */
    disable(): void
    {
        this.disabled = true;
        this.rangeGroup.disable();
    }

    /**
     * @desc - enable the date range
     * @access - public
     * @param -
     * @return -
     */
    enable(): void
    {
        this.disabled = false;
        this.rangeGroup.enable();
    }

    /**
     * @desc - clear the date range
     * @access - public
     * @param -
     * @return -
     */
    clear(): void
    {
        this.startControl.setValue(null, { emitEvent: false });
        this.endControl.setValue(null, { emitEvent: false });

        this.clearPressed.emit();
        this.dateRangeChange.emit({ start: null, end: null });
    }

    /**
     * @desc   - clears without emitting
     * @access - public
     * @param  -
     * @return -
     */
    clearWithoutEmitting(): void 
    {
        this.startControl.setValue(null, { emitEvent: false });
        this.endControl.setValue(null, { emitEvent: false });
    }
}

export { DateRangePickerComponent };
